// ProviderListView.jsx
import React, { useState } from "react";
import { Card, CardContent, Typography } from "@mhome/ui";
import DownloadDoneIcon from "@mui/icons-material/DownloadDone";
import ProviderFilter from "./provider-filter";
import ProviderDetailDialog from "./provider-detail-dialog";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import {
  disconnectIntegration,
  installIntegration,
  startConnectIntegration,
  uninstallIntegration,
} from "../service/integration-service";
import { useToast } from "../common/toast";
import { useWebSocket } from "../ws/websocket";
import ConnectDialog from "./connect-dialog";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import ResponsiveCardGrid from "../common/responsive-card-grid";

const ProviderCard = ({ provider, getComponentBgColor, onSelect }) => {
  // Function to determine card status and colors
  const getCardStatus = () => {
    if (!provider.installed) {
      return {
        background: getComponentBgColor("hsl(var(--muted))"),
        textColor: "textPrimary",
      };
    }

    if (provider.multiConnect) {
      const activeCount = provider.connects.filter(
        (x) => x.status === "active"
      ).length;
      const totalCount = provider.connects.length;
      const allHealthy = activeCount === totalCount;

      return {
        background: allHealthy
          ? getComponentBgColor("hsl(var(--card))")
          : getComponentBgColor("hsl(var(--accent))"),
        textColor: allHealthy ? "textPrimary" : "hsl(var(--warning))",
        count: {
          active: activeCount,
          total: totalCount,
        },
      };
    }

    return {
      background: getComponentBgColor("hsl(var(--card))"),
      textColor: "textPrimary",
    };
  };

  const status = getCardStatus();

  return (
    <Card
      elevation={0}
      className="h-[90px] border-none cursor-pointer transition-all duration-200 select-none"
      style={{
        backgroundColor: status.background,
        WebkitTapHighlightColor: "transparent",
        touchAction: "manipulation",
      }}
      onClick={() => onSelect(provider)}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = status.background;
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.backgroundColor = "hsl(var(--accent))";
      }}
    >
      <CardContent className="p-3 h-full flex flex-col">
        <div className="flex items-center justify-between">
          <Typography
            variant="subtitle2"
            noWrap
            className="font-medium text-sm leading-tight"
          >
            {provider.name}
          </Typography>
          <div className="flex gap-2 items-center">
            {provider.hub && (
              <DeviceHubIcon
                style={{
                  fontSize: 16,
                  color: "hsl(var(--primary))",
                }}
              />
            )}
            {provider.installed && (
              <DownloadDoneIcon
                style={{
                  fontSize: 16,
                  color: "#4caf50",
                }}
              />
            )}
          </div>
        </div>
        <Typography
          variant="caption"
          color="textSecondary"
          noWrap
          className="mb-auto"
        >
          {provider.org}
        </Typography>
        <Typography
          variant="caption"
          className="font-semibold"
          style={{
            color: status.textColor,
          }}
        >
          {provider.installed ? (
            provider.multiConnect ? (
              <>
                <span style={{ color: status.textColor }}>
                  {status.count?.active}
                </span>
                {" / "}
                {status.count?.total} Connection
                {status.count?.total !== 1 ? "s" : ""}
              </>
            ) : (
              "Connected"
            )
          ) : (
            "Not Connected"
          )}
        </Typography>
      </CardContent>
    </Card>
  );
};

const ProviderListView = ({ providers, showFilter, onLoadIntegration }) => {
  const [providerFilterText, setProviderFilterText] = useState("");
  const [providerSortBy, setProviderSortBy] = useState("alphabet");
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [openConnect, setOpenConnect] = useState(false);
  const [connectIntegrationName, setConnectIntegrationName] = useState("");
  const [connectIntegrationId, setConnectIntegrationId] = useState("");
  const [connectInfo, setConnectInfo] = useState(null);
  const { sendAndWaitWs } = useWebSocket();
  const { successToast, errorToast } = useToast();

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  const handleProviderInstall = async (provider) => {
    if (provider.installType === "connect") {
      await handleConnect(provider.integration);
    } else {
      await handleInstall(provider.integration);
    }
  };

  const handleProviderUninstall = async (provider, connectId) => {
    if (provider.installType === "connect") {
      await handleDisconnect(provider.integration, connectId);
    } else {
      await handleUninstall(provider.integration);
    }
  };

  const handleInstall = async (integration) => {
    const resp = await installIntegration(sendAndWaitWs, integration);
    if (resp && !resp.error) {
      successToast("Installed");
      onLoadIntegration();
      setSelectedProvider(null);
    } else {
      errorToast("Error!");
    }
  };

  const handleUninstall = async (integration) => {
    const resp = await uninstallIntegration(sendAndWaitWs, integration);
    if (resp) {
      successToast("Uninstalled");
      onLoadIntegration();
      setSelectedProvider(null);
    } else {
      errorToast("Error!");
    }
  };

  const handleConnectComplete = async () => {
    successToast("Connection Added");
    onLoadIntegration();
    setSelectedProvider(null);
  };

  const handleConnect = async (integration) => {
    const resp = await startConnectIntegration(sendAndWaitWs, integration);
    if (resp && !resp.error) {
      setConnectInfo(resp);
      const provider = providers.find((p) => p.integration === integration);
      setConnectIntegrationName(provider ? provider.name : integration);
      setConnectIntegrationId(provider ? provider.integration : integration);
      setOpenConnect(true);
    } else {
      errorToast("Error!");
    }
  };

  const handleDisconnect = async (integration, connectId) => {
    const resp = await disconnectIntegration(
      sendAndWaitWs,
      integration,
      connectId
    );
    if (resp) {
      successToast("Disconnected");
      onLoadIntegration();
      setSelectedProvider(null);
    } else {
      errorToast("Error!");
    }
  };

  const sortedProviders = providers
    .filter(
      (provider) =>
        provider.name
          .toLowerCase()
          .includes(providerFilterText.toLowerCase()) &&
        provider.name.toLowerCase() !== "gove" // TODO: remove
    )
    .sort((a, b) => {
      if (providerSortBy === "alphabet") {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aIsLetter = /^[a-zA-Z]/.test(aName);
        const bIsLetter = /^[a-zA-Z]/.test(bName);
        if (aIsLetter && !bIsLetter) return -1;
        if (!aIsLetter && bIsLetter) return 1;
        return aName.localeCompare(bName);
      }
      return 0;
    });

  return (
    <div>
      {showFilter && (
        <ProviderFilter
          sortBy={providerSortBy}
          setSortBy={setProviderSortBy}
          setFilterText={setProviderFilterText}
        />
      )}
      <ResponsiveCardGrid spacing={1.5} breakpoints={{ xs: 2, sm: 3, md: 4 }}>
        {sortedProviders.map((provider) => (
          <ProviderCard
            key={provider.integration}
            provider={provider}
            getComponentBgColor={getComponentBgColor}
            onSelect={setSelectedProvider}
          />
        ))}
      </ResponsiveCardGrid>
      <ProviderDetailDialog
        open={Boolean(selectedProvider)}
        provider={selectedProvider}
        onClose={() => {
          setSelectedProvider(null);
        }}
        onDisconnect={handleProviderUninstall}
        onNewConnect={handleProviderInstall}
      />
      <ConnectDialog
        open={openConnect}
        integrationId={connectIntegrationId}
        integrationName={connectIntegrationName}
        connectInfo={connectInfo}
        onClose={() => setOpenConnect(false)}
        onSuccess={handleConnectComplete}
        onFailure={() => errorToast("Connect Error!")}
      />
    </div>
  );
};

export default ProviderListView;
